import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/useAuthStore';
import { trackPageview, identifyUser, setOrgGroup } from './lib/analytics';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import RouteSuspense from './components/shared/RouteSuspense';
// Eager: the two first-paint surfaces (marketing front door + sign-in) and the
// app shell. Everything else is route-level code-split (2026-06-11) — the main
// bundle had grown to ~2.5 MB with every page (recharts, xyflow, …) in one
// chunk; now each page's code downloads when its route is first visited.
import { lazyRetry, clearChunkReloadLatch } from './lib/lazyRetry';
import MarketingHomePage from './pages/MarketingHomePage';
import LoginPage from './pages/LoginPage';

const SetupPage = lazyRetry(() => import('./pages/SetupPage'));
const ForgotPasswordPage = lazyRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazyRetry(() => import('./pages/VerifyEmailPage'));
const PipelinePage = lazyRetry(() => import('./pages/PipelinePage'));
const ResourcesPage = lazyRetry(() => import('./pages/ResourcesPage'));
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'));
const AdminLayout = lazyRetry(() => import('./pages/admin/AdminLayout'));
const SettingsHomePage = lazyRetry(() => import('./pages/admin/SettingsHomePage'));
const JiraSetupPage = lazyRetry(() => import('./pages/admin/JiraSetupPage'));
const AzureSetupPage = lazyRetry(() => import('./pages/admin/AzureSetupPage'));
const AsanaSetupPage = lazyRetry(() => import('./pages/admin/AsanaSetupPage'));
const StatusMappingPage = lazyRetry(() => import('./pages/admin/StatusMappingPage'));
const FieldMappingPage = lazyRetry(() => import('./pages/admin/FieldMappingPage'));
const BrandingPage = lazyRetry(() => import('./pages/admin/BrandingPage'));
const SignalCatalogPage = lazyRetry(() => import('./pages/admin/SignalCatalogPage'));
const RolesPage = lazyRetry(() => import('./pages/admin/RolesPage'));
const IssueTypesPage = lazyRetry(() => import('./pages/admin/IssueTypesPage'));
const CadencePage = lazyRetry(() => import('./pages/admin/CadencePage'));
const ProjectsAdminPage = lazyRetry(() => import('./pages/admin/ProjectsAdminPage'));
const ImportExportPage = lazyRetry(() => import('./pages/admin/ImportExportPage'));
const FilesPage = lazyRetry(() => import('./pages/FilesPage'));
const DataSourcesPage = lazyRetry(() => import('./pages/DataSourcesPage'));
const ConnectSourceWizard = lazyRetry(() => import('./pages/admin/ConnectSourceWizard'));
const DocsPage = lazyRetry(() => import('./pages/DocsPage'));
const QaLedgerPage = lazyRetry(() => import('./pages/QaLedgerPage'));
const ResourceDetailPage = lazyRetry(() => import('./pages/ResourceDetailPage'));
const TimeCardPage = lazyRetry(() => import('./pages/TimeCardPage'));
const SuperAdminPage = lazyRetry(() => import('./pages/SuperAdminPage'));
const IntelligencePage = lazyRetry(() => import('./pages/IntelligencePage'));
const TheBriefPage = lazyRetry(() => import('./pages/TheBriefPage'));
const KnowledgeGraphPage = lazyRetry(() => import('./pages/KnowledgeGraphPage'));
const ReportsPage = lazyRetry(() => import('./pages/ReportsPage'));
const OrgSelectorPage = lazyRetry(() => import('./pages/OrgSelectorPage'));
const NotFoundPage = lazyRetry(() => import('./pages/NotFoundPage'));
const AiUsagePage = lazyRetry(() => import('./pages/admin/AiUsagePage'));
const ApiKeysPage = lazyRetry(() => import('./pages/admin/ApiKeysPage'));
const AuditLogPage = lazyRetry(() => import('./pages/admin/AuditLogPage'));
const WebhooksPage = lazyRetry(() => import('./pages/admin/WebhooksPage'));
const AutomationsPage = lazyRetry(() => import('./pages/admin/AutomationsPage'));
const MembersPage = lazyRetry(() => import('./pages/admin/MembersPage'));
const MyWorkspacesPage = lazyRetry(() => import('./pages/admin/MyWorkspacesPage'));
const BillingPage = lazyRetry(() => import('./pages/admin/BillingPage'));
const InviteAcceptPage = lazyRetry(() => import('./pages/InviteAcceptPage'));
const NotificationsSettingsPage = lazyRetry(() => import('./pages/NotificationsSettingsPage'));
const AccountSettingsPage = lazyRetry(() => import('./pages/AccountSettingsPage'));
const SeoPlaceholderPage = lazyRetry(() => import('./pages/SeoPlaceholderPage'));
const SignalLearnPage = lazyRetry(() => import('./pages/SignalLearnPage'));
const PersonaLearnPage = lazyRetry(() => import('./pages/PersonaLearnPage'));
const LearnIndexPage = lazyRetry(() => import('./pages/LearnIndexPage'));
const ForIndexPage = lazyRetry(() => import('./pages/ForIndexPage'));
const BlogIndexPage = lazyRetry(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazyRetry(() => import('./pages/BlogPostPage'));
const FoundersPage = lazyRetry(() => import('./pages/FoundersPage'));
const DemoPage = lazyRetry(() => import('./pages/DemoPage'));
const PrivacyPage = lazyRetry(() => import('./pages/PrivacyPage'));


function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

// Root-path guard. Logged-out visitors at the bare "/" get the public marketing
// homepage (the new front door); logged-out visitors at any app sub-route under
// "/" still go to sign in; logged-in users get the app shell. This is what
// flips "/" from "redirect to login" to "show the marketing site" without
// un-gating the app routes.
function RootRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  const { pathname } = useLocation();
  if (!token) {
    return pathname === '/' ? <MarketingHomePage /> : <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Guards the admin-only settings SUB-pages (an Outlet-style layout guard). The
// Settings hub index (/admin) is deliberately OUTSIDE this guard: since
// 2026-07-01 every access level can open the hub — non-admins see only the
// user-scoped "Your account" group (lib/settingsNav.ts settingsNavForRole), so
// account settings are findable from the sidebar + settings search, not just
// the header profile dropdown. A non-admin who deep-links an admin sub-page
// lands back on the hub. Client-side UX only — admin APIs enforce requireAdmin
// server-side regardless.
function AdminRoute() {
  const { token, isAdmin } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/admin" replace />;
  return <Outlet />;
}

// Landing redirect for the bare "/" path. ALL viewports land on /brief — The
// Brief is the answer surface (Brian, 2026-07-20: the brief is the opening
// page, not the workload page) — phones render the full desktop app (Layout
// pins a 1280px viewport; see Layout.tsx). The prior phone→/today
// mobile-briefing fork was removed 2026-06-06; a dedicated mobile experience
// will be reimagined as its own project rather than carried by the
// half-built /today surface.
function HomeRedirect() {
  return <Navigate to="/brief" replace />;
}

// /dashboard → /capacity (2026-05-25 rename). Preserves the search string so
// bookmarks like /dashboard?tab=availability land on the equivalent tab.
function CapacityRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/capacity${search}`} replace />;
}

// /rollup retired into Workload's own Epics tab (Work Center consolidation
// slice W1, docs/design/work-center-hifi.html state A, approved 2026-07-20)
// — a bookmarked/shared /rollup(?epics=…) link lands on the Epics tab's
// Epic-scope lens, every other param (notably `?epics=`) preserved. Exported
// (mirrors `ScrollToTop` below) so client/src/__tests__/AppRedirects.test.tsx
// can pin the redirect target without rendering the whole App tree.
export function RollupRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'epics');
  params.set('lens', 'scope');
  return <Navigate to={`/pipeline?${params.toString()}`} replace />;
}

// /my-week retired into Workload's own "My Week" tab (same consolidation,
// hi-fi state A/E) — a bookmarked /my-week link lands on that tab.
export function MyWeekRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'my-week');
  return <Navigate to={`/pipeline?${params.toString()}`} replace />;
}

// Resets window scroll to the top on every route change. This app uses
// React-Router's <Routes> (not a data router), so the built-in
// <ScrollRestoration> isn't available and SPA navigation otherwise keeps the
// previous page's scroll offset — opening a deep content page (e.g.
// /learn/velocity-drift) from a scrolled position rendered it mid-page. Guards
// hash links so in-page anchors (#section) still jump where expected.
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Sends a PostHog $pageview on every route change (needed because this is a
// single-page app) and identifies the user + their org once logged in.
function AnalyticsBridge() {
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const org = useAuthStore(s => s.org);

  useEffect(() => { trackPageview(); }, [location.pathname]);
  useEffect(() => {
    if (user) {
      identifyUser(user.id, { email: user.email, name: user.name });
      if (org) setOrgGroup(org.id, org.name);
    }
  }, [user?.id, org?.id]);

  return null;
}

export default function App() {
  // The app mounted — chunks resolved, so re-arm the stale-chunk reload latch.
  useEffect(() => { clearChunkReloadLatch(); }, []);
  return (
    <>
    <ScrollToTop />
    <AnalyticsBridge />
    <RouteSuspense>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/select-org" element={<PrivateRoute><OrgSelectorPage /></PrivateRoute>} />
      {/* SEO landing-page routes — PUBLIC (no PrivateRoute). PR 2 ships
          /learn/* content for 3 Signals (sprint-risk-forecast,
          velocity-drift, over-allocation) via SignalLearnPage, which
          looks up the slug in signalLearnContent.ts and falls back to
          SeoPlaceholderPage for the 9 sitemap-claimed slugs whose
          content ships in PR 3. /for/* persona pages remain on the
          placeholder until PR 3. */}
      <Route path="/learn" element={<LearnIndexPage />} />
      <Route path="/learn/:slug" element={<SignalLearnPage />} />
      <Route path="/for" element={<ForIndexPage />} />
      <Route path="/for/:slug" element={<PersonaLearnPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/founders" element={<FoundersPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/" element={
        <RootRoute>
          {/* Outer boundary — catches crashes in Layout itself (Sidebar /
              Header / tour effects). The inner ErrorBoundary inside Layout
              only catches errors in the routed <Outlet />. Without this
              wrapper a render crash in Layout unmounted the whole React
              tree → blank white page that even refresh couldn't recover
              (May 2026 tour debug). */}
          <ErrorBoundary>
            <Layout />
          </ErrorBoundary>
        </RootRoute>
      }>
        <Route index element={<HomeRedirect />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="resources/:id" element={<ResourceDetailPage />} />
        <Route path="capacity" element={<DashboardPage />} />
        {/* Back-compat: /dashboard was renamed to /capacity on 2026-05-25
            when the section was scoped down to capacity planning. Preserves
            the query string so bookmarks like /dashboard?tab=availability
            still land on the right Capacity tab. */}
        <Route path="dashboard" element={<CapacityRedirect />} />
        <Route path="intelligence" element={<IntelligencePage />} />
        <Route path="brief" element={<TheBriefPage />} />
        <Route path="knowledge-graph" element={<KnowledgeGraphPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="rollup" element={<RollupRedirect />} />
        {/* Back-compat: Explore folded into Reports as its last tab (2026-06-11);
            old bookmarks land on the same view. */}
        <Route path="explore" element={<Navigate to="/reports?tab=explore" replace />} />
        {/* Connect, Data Sources, and the user-scoped account pages are
            conceptually Settings (they live in the settingsNav groups), so they
            render inside AdminLayout to keep the grouped settings rail visible
            while you're on them — the rail no longer vanishes the moment you
            leave an /admin/* page. A PATHLESS layout route preserves their
            top-level URLs (/connect, /data, /settings/*) and their current
            access (no AdminRoute guard — account settings are for every role;
            the rail itself is access-aware via settingsNavForRole). */}
        <Route element={<AdminLayout />}>
          <Route path="data" element={<DataSourcesPage />} />
          <Route path="connect" element={<ConnectSourceWizard />} />
          <Route path="settings/account" element={<AccountSettingsPage />} />
          <Route path="settings/notifications" element={<NotificationsSettingsPage />} />
        </Route>
        <Route path="docs" element={<DocsPage />} />
        <Route path="import" element={<ImportExportPage />} />
        <Route path="files" element={<FilesPage />} />
        {/* The snapshot save/load feature was removed 2026-06-27; "Start Fresh"
            now lives on the Data Sources settings page. Old links redirect there. */}
        <Route path="snapshots" element={<Navigate to="/data" replace />} />
        <Route path="projects" element={<Navigate to="/data" replace />} />
        <Route path="timecard" element={<TimeCardPage />} />
        <Route path="my-week" element={<MyWeekRedirect />} />
        <Route path="super-admin" element={<SuperAdminPage />} />
        {/* The Law 6 sign-off ledger — deliberately a top-level /qa route (NOT
            nested under /admin or AdminLayout's settings-rail chrome), since it
            renders full-viewport in its own iframe. A separate, standalone
            AdminRoute guard (same component as the /admin/* sub-pages below) so
            the path stays exactly /qa — what scripts/deploy.sh opens after every
            staging deploy. */}
        <Route element={<AdminRoute />}>
          <Route path="qa" element={<QaLedgerPage />} />
        </Route>
        {/* The hub index is open to every signed-in role (the Settings space is
            for everyone; non-admins see only "Your account"); the admin
            sub-pages sit behind the pathless AdminRoute guard below. */}
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<SettingsHomePage />} />
          <Route element={<AdminRoute />}>
            <Route path="jira" element={<JiraSetupPage />} />
            <Route path="azure" element={<AzureSetupPage />} />
            <Route path="asana" element={<AsanaSetupPage />} />
            <Route path="phases" element={<StatusMappingPage />} />
            <Route path="fields" element={<FieldMappingPage />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="signals" element={<SignalCatalogPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="issue-types" element={<IssueTypesPage />} />
            {/* Back-compat: old /admin/hierarchy URL redirects to the new path. */}
            <Route path="hierarchy" element={<Navigate to="/admin/issue-types" replace />} />
            <Route path="cadence" element={<CadencePage />} />
            <Route path="projects" element={<ProjectsAdminPage />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="workspaces" element={<MyWorkspacesPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="ai-usage" element={<AiUsagePage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="automations" element={<AutomationsPage />} />
            {/* Catch unmatched /admin/* (e.g. a removed page whose nav link was
                left orphaned) so the admin Outlet shows a recoverable 404 panel
                instead of a blank screen. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
        {/* App-level catch-all — any other unmatched authenticated route lands
            on the 404 panel (sidebar/header stay usable) rather than blank. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </RouteSuspense>
    </>
  );
}
